// ============================================================================
// RECORD A STATUTORY DUE-DATE EXTENSION (ADR-0021 §6)
// ============================================================================
// Records what a notification SAYS. It moves no deadline by itself — applying
// is a separate, explicit step, so the scope can be previewed before hundreds
// of dates change.
//
// The form is deliberately scope-first: the three fields that decide WHO is
// covered (return, period, and the optional state / scheme narrowing) sit
// together, because getting the scope wrong is the failure that matters. A
// wrong date is visible on the preview; a wrong scope silently reaches the
// wrong clients.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { gstExtensionService } from 'api/services/gstExtensionService';
import type {
  CreateObligationExtensionRequest,
  ObligationExtension,
  ObligationState,
  ObligationStateCohort,
  ObligationStatutoryDates,
} from 'api/types/obligationExtension.types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the recorded extension so the caller can offer a preview. */
  onSaved: (created: ObligationExtension) => void;
}

/**
 * What the extended date has to beat, shown under the field.
 *
 * Three states worth distinguishing, because they mean different things:
 * one deadline in scope, SEVERAL (so the date can only partly extend it), or
 * none at all (no due-date rule for the form — its own problem, not a bad date).
 */
const StatutoryHint: React.FC<{
  statutory: ObligationStatutoryDates | null;
  newDueDate: string;
  schemeCode: string;
}> = ({ statutory, newDueDate, schemeCode }) => {
  if (!statutory) return null;

  const { dates } = statutory;

  if (dates.length === 0) {
    return (
      <div style={{ fontSize: '0.7rem', color: '#d97706', marginTop: 4 }}>
        No due-date rule covers this return and period, so there is no statutory date to
        extend. The extension will record, but it will reach nothing until a rule exists.
      </div>
    );
  }

  const earliest = dates[0];
  const latest = dates[dates.length - 1];

  // Between the earliest and the latest: legitimate, and sometimes exactly what
  // a notification says — but the user should know it is partial before they
  // reach the preview and wonder why half the list was skipped.
  const partial = newDueDate
    && newDueDate >= earliest.slice(0, 10)
    && newDueDate < latest.slice(0, 10);

  return (
    <div style={{ fontSize: '0.7rem', marginTop: 4, color: partial ? '#d97706' : 'var(--text-secondary)' }}>
      {dates.length === 1
        ? <>Statutory deadline: <strong>{formatDate(earliest)}</strong>.</>
        : (
          <>
            Statutory deadlines in scope: <strong>{dates.map((d) => formatDate(d)).join(' · ')}</strong>
            {' '}— they differ by state and scheme.
          </>
        )}
      {partial && ' This date extends only the earlier ones; the rest will be skipped.'}

      {/* Only where it can actually bite. A monthly-scoped extension in
          November is perfectly normal; a QRMP-scoped one reaches nothing,
          because quarterly periods are matched on their FIRST month — and the
          statutory date above would still look plausible, which is what makes
          this worth saying. */}
      {!statutory.isQuarterStart && schemeCode !== 'MONTHLY' && (
        <>
          {' '}This month does not begin a tax quarter (April, July, October, January), so the
          quarterly part of this scope would reach nothing.
        </>
      )}
    </div>
  );
};

/**
 * The returns GST generates obligations for. GSTR-9 / 9C are absent because no
 * annual obligation is generated yet — offering a scope that can never match
 * would produce an extension that silently covers nothing.
 */
const FORM_OPTIONS = [
  { value: 'GSTR1', label: 'GSTR-1 — outward supplies' },
  { value: 'GSTR3B', label: 'GSTR-3B — summary return' },
  { value: 'IFF', label: 'IFF — invoice furnishing (QRMP months 1-2)' },
  { value: 'PMT06', label: 'PMT-06 — QRMP monthly payment' },
];

/**
 * The SCHEME the period was generated under — not the period's shape. An IFF
 * period is monthly in shape but belongs to the QRMP scheme, and a notification
 * that extends "QRMP taxpayers" means the scheme.
 */
const SCHEME_OPTIONS = [
  { value: '', label: 'Every frequency' },
  { value: 'MONTHLY', label: 'Monthly filers only' },
  { value: 'QRMP', label: 'QRMP filers only' },
];

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

interface FormState {
  notificationRef: string;
  issuedOn: string;
  formCode: string;
  /** `YYYY-MM` from the month input; sent as the first of that month. */
  periodMonth: string;
  newDueDate: string;
  schemeCode: string;
  stateCodes: string[];
  scopeNote: string;
}

const INITIAL: FormState = {
  notificationRef: '',
  issuedOn: '',
  formCode: 'GSTR3B',
  periodMonth: '',
  newDueDate: '',
  schemeCode: '',
  stateCodes: [],
  scopeNote: '',
};

const GstExtensionModal: React.FC<Props> = ({ isOpen, onClose, onSaved }) => {
  const [form, setForm] = useState<FormState>(INITIAL);
  const initialSnapshotRef = useRef('');

  const [states, setStates] = useState<ObligationState[]>([]);
  const [cohorts, setCohorts] = useState<ObligationStateCohort[]>([]);

  // The statutory deadline(s) this scope would replace. Shown beside the date
  // field so the extended date is typed against something rather than from
  // memory — the cheapest way to stop the wrong-month typo.
  const [statutory, setStatutory] = useState<ObligationStatutoryDates | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors((prev) => {
      if (!prev[f]) return prev;
      const next = { ...prev };
      delete next[f];
      return next;
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const data = { ...INITIAL };
    setForm(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});

    void (async () => {
      try {
        const scope = await gstExtensionService.getStateScope();
        setStates(scope.states);
        setCohorts(scope.cohorts);
      } catch {
        // A failed state list is not fatal: nationwide is the commonest scope
        // and needs no states at all. Say so rather than blocking the form.
        setStates([]);
        setCohorts([]);
        notificationService.toast.warning(
          'Could not load the state list — you can still record a nationwide extension.',
        );
      }
    })();
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(form) !== initialSnapshotRef.current;
  }, [form]);

  // Re-resolved whenever the SCOPE changes — not when the date changes, because
  // the date is what gets compared against this, not an input to it.
  const scopeKey = `${form.formCode}|${form.periodMonth}|${form.schemeCode}|${form.stateCodes.join(',')}`;

  useEffect(() => {
    if (!isOpen || !form.formCode || !form.periodMonth) {
      setStatutory(null);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const result = await gstExtensionService.getStatutoryDates({
            formCode: form.formCode,
            periodStart: `${form.periodMonth}-01`,
            schemeCode: form.schemeCode || null,
            stateCodes: form.stateCodes.length > 0 ? form.stateCodes : null,
          });
          if (!cancelled) setStatutory(result);
        } catch {
          // A hint that fails to load must not block recording. The server
          // refuses an unreachable date on save regardless.
          if (!cancelled) setStatutory(null);
        }
      })();
    }, 250);

    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, scopeKey]);

  // Which cohort each state sits in, so the option can say the DAY rather than
  // an internal code. Built from the cohorts' own state lists — there is no
  // group code to translate any more, because a cohort IS a rule.
  const cohortByState = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cohorts) {
      for (const code of c.stateCodes) map.set(code, c.label);
    }
    return map;
  }, [cohorts]);

  const stateOptions = useMemo(
    () => states.map((s) => {
      // Leading with the tax code, because whoever is scoping a notification is
      // reading a GSTIN or a circular that names states by that code.
      const deadline = cohortByState.get(s.stateCode);
      return {
        value: s.stateCode,
        label: `${s.stateCode} — ${s.stateName ?? 'Unnamed'}${deadline ? ` · ${deadline}` : ''}`,
      };
    }),
    [states, cohortByState],
  );

  // A notification usually names a whole cohort, so selecting one is the common
  // path. The cohort carries its own state list — including the fall-through,
  // already expanded server-side to the states nobody named.
  const selectCohort = useCallback((cohort: ObligationStateCohort) => {
    setForm((prev) => ({ ...prev, stateCodes: [...cohort.stateCodes] }));
  }, []);

  const patch = useCallback((changes: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...changes }));
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};

    if (!form.notificationRef.trim()) {
      // Required by the API too. An extended date with no citation cannot be
      // defended when a late-fee notice arrives, which is most of the reason to
      // record one at all.
      errors.notificationRef = 'A notification reference is required';
    }
    if (!form.formCode) errors.formCode = 'Choose the return this extends';
    if (!form.periodMonth) errors.periodMonth = 'Choose the tax period it covers';
    if (!form.newDueDate) errors.newDueDate = 'Enter the extended due date';

    if (form.periodMonth && form.newDueDate && form.newDueDate <= `${form.periodMonth}-01`) {
      // The database rejects this too, but failing here says which field is
      // wrong instead of surfacing a constraint name.
      errors.newDueDate = 'The due date must fall after the period it reports on';
    }

    // Earlier than EVERY statutory date in scope means it extends nothing. The
    // server refuses this as well and is authoritative; catching it here just
    // saves a round trip and points at the field.
    //
    // Only the earliest. A date between the earliest and the latest is
    // genuinely partial — a notification can extend one state group and not
    // the other — so that is the warning shown below the field, not an error.
    const earliest = statutory?.dates?.[0];
    if (!errors.newDueDate && earliest && form.newDueDate && form.newDueDate < earliest.slice(0, 10)) {
      errors.newDueDate =
        `Earlier than the statutory deadline (${formatDate(earliest)}), so it would extend nothing. `
        + 'Check the month and year.';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.querySelector<HTMLElement>('input')?.focus();
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form, statutory]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const request: CreateObligationExtensionRequest = {
        notificationRef: form.notificationRef.trim(),
        issuedOn: form.issuedOn || null,
        moduleCode: 'GST',
        formCode: form.formCode,
        periodStart: `${form.periodMonth}-01`,
        newDueDate: form.newDueDate,
        // Undefined, never []. An empty array matches nobody while looking like
        // a filter, and the database rejects it outright.
        stateCodes: form.stateCodes.length > 0 ? form.stateCodes : null,
        schemeCode: form.schemeCode || null,
        scopeNote: form.scopeNote.trim() || null,
      };

      const created = await gstExtensionService.create(request);
      notificationService.toast.success(
        `${created.notificationRef} recorded. Preview it before applying.`,
      );
      onSaved(created);
      onClose();
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not record the extension.',
      );
    } finally {
      setIsSaving(false);
    }
  }, [form, onSaved, onClose]);

  if (!isOpen) return null;

  const scopeSummary = (() => {
    const where = form.stateCodes.length > 0
      ? `${form.stateCodes.length} state(s)`
      : 'every state';
    const who = form.schemeCode ? `${form.schemeCode} filers` : 'every scheme';
    return `${form.formCode || 'a return'} · ${where} · ${who}`;
  })();

  return (
    <>
      <div className="modal-backdrop" onClick={handleClose} />
      <div className="modal-container modal-lg">
        {isSaving && (
          <div
            style={{
              position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Recording…
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Record a due-date extension</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <button className="modal-close" onClick={handleClose} title="Close">
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div
            style={{
              border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
              borderRadius: 6, padding: '0.625rem 0.75rem', marginBottom: 14,
              fontSize: '0.8125rem', color: 'var(--text-secondary)',
            }}
          >
            Recording changes no deadline. You will be shown exactly which obligations this
            covers, and nothing moves until you apply it.
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="notificationRef">
              <label className="form-label">
                Notification reference <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-input${getFieldError('notificationRef') ? ' form-field-error' : ''}`}
                value={form.notificationRef}
                placeholder="e.g. 07/2026 – Central Tax"
                onChange={(e) => { clearFieldError('notificationRef'); patch({ notificationRef: e.target.value }); }}
              />
              {getFieldError('notificationRef')
                ? <div className="form-error-message">{getFieldError('notificationRef')}</div>
                : (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                    The citation a late-fee notice will be answered with.
                  </div>
                )}
            </div>

            <div className="form-group" data-field="issuedOn">
              <label className="form-label">Issued on</label>
              <input
                type="date"
                className="form-input"
                value={form.issuedOn}
                onChange={(e) => patch({ issuedOn: e.target.value })}
              />
            </div>
          </div>

          <h3
            style={{
              fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)',
              margin: '14px 0 8px', paddingBottom: 4, borderBottom: '1px solid var(--border-color)',
            }}
          >
            What it covers
          </h3>

          <div className="form-grid-3">
            <div className="form-group" data-field="formCode">
              <SyutSelect
                label={<>Return <span className="text-danger">*</span></>}
                options={FORM_OPTIONS}
                value={form.formCode}
                isMulti={false}
                isClearable={false}
                size="sm"
                error={getFieldError('formCode')}
                onChange={(v) => { clearFieldError('formCode'); patch({ formCode: (v as string) || '' }); }}
              />
            </div>

            <div className="form-group" data-field="periodMonth">
              <label className="form-label">
                Tax period <span className="text-danger">*</span>
              </label>
              <input
                type="month"
                className={`form-input${getFieldError('periodMonth') ? ' form-field-error' : ''}`}
                value={form.periodMonth}
                onChange={(e) => { clearFieldError('periodMonth'); patch({ periodMonth: e.target.value }); }}
              />
              {getFieldError('periodMonth')
                ? <div className="form-error-message">{getFieldError('periodMonth')}</div>
                : (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                    For a quarter, pick its FIRST month — periods are matched on their start date.
                  </div>
                )}
            </div>

            <div className="form-group" data-field="newDueDate">
              <label className="form-label">
                Extended due date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className={`form-input${getFieldError('newDueDate') ? ' form-field-error' : ''}`}
                value={form.newDueDate}
                onChange={(e) => { clearFieldError('newDueDate'); patch({ newDueDate: e.target.value }); }}
              />
              {getFieldError('newDueDate')
                ? <div className="form-error-message">{getFieldError('newDueDate')}</div>
                : (
                  <StatutoryHint
                    statutory={statutory}
                    newDueDate={form.newDueDate}
                    schemeCode={form.schemeCode}
                  />
                )}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="schemeCode">
              <SyutSelect
                label="Filing frequency"
                options={SCHEME_OPTIONS}
                value={form.schemeCode}
                isMulti={false}
                isClearable={false}
                size="sm"
                onChange={(v) => patch({ schemeCode: (v as string) || '' })}
              />
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                The scheme the period was generated under, not its shape — an IFF period is
                monthly in shape but belongs to QRMP.
              </div>
            </div>

            <div className="form-group" data-field="stateCodes">
              <SyutSelect
                label="States"
                variant="checkbox"
                isMulti
                showSelectAll
                selectAllLabel="All states"
                searchPlaceholder="Search state…"
                options={stateOptions}
                value={form.stateCodes}
                size="sm"
                placeholder="Every state (nationwide)"
                onChange={(v) => patch({ stateCodes: (v as string[]) ?? [] })}
              />
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                  Leave empty for nationwide.
                </span>
                {/* One button per deadline cohort, labelled by the day it
                    produces. The fall-through reads "the rest" because that is
                    literally what it is — the states no rule named. */}
                {cohorts.map((c) => (
                  <button
                    key={c.ruleId}
                    type="button"
                    className="btn btn-secondary"
                    style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                    onClick={() => selectCohort(c)}
                    title={
                      `Select all ${c.stateCodes.length} states where ${c.label}`
                      + (c.isFallThrough ? ' — these are the states no rule names individually' : '')
                      + (c.notificationRef ? ` (${c.notificationRef})` : '')
                    }
                  >
                    {c.isFallThrough ? `The rest — ${c.label}` : c.label} ({c.stateCodes.length})
                  </button>
                ))}
                {form.stateCodes.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                    onClick={() => patch({ stateCodes: [] })}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="form-group" data-field="scopeNote">
            <label className="form-label">Scope note</label>
            <textarea
              className="form-textarea"
              rows={2}
              value={form.scopeNote}
              placeholder="Anything about the scope the fields above cannot express."
              onChange={(e) => patch({ scopeNote: e.target.value })}
            />
          </div>

          <div
            style={{
              border: '1px solid var(--border-color)', borderRadius: 6,
              padding: '0.5rem 0.75rem', fontSize: '0.8125rem', color: 'var(--text-secondary)',
            }}
          >
            Will cover: <strong style={{ color: 'var(--text-primary)' }}>{scopeSummary}</strong>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>Record</button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Record this extension?"
        message={
          `${form.notificationRef || 'This notification'} will be recorded for ${scopeSummary}. `
          + 'No deadline moves until you apply it.'
        }
        confirmLabel="Record"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={() => { void handleConfirmSave(); }}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default GstExtensionModal;
